import React, { useState, useEffect } from 'react';
import { submitInquiry, getSettings } from '../api/services';
import Header from '../components/Header/Header';
import Footer from '../components/Footer/Footer';
import './ContactPage.css';

const INITIAL_FORM = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  inquiry_type: 'general',
};

// Fallback used only if settings haven't loaded yet / value not set in admin
const DEFAULT_WHATSAPP_NUMBER = '919981206128';

function ContactPage() {
  const [form, setForm]       = useState(INITIAL_FORM);
  const [errors, setErrors]   = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted]   = useState(false);
  const [inquiryNo, setInquiryNo]   = useState('');
  const [serverError, setServerError] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState(DEFAULT_WHATSAPP_NUMBER);

  useEffect(() => {
    getSettings()
      .then((res) => {
        if (res.success && res.data && res.data.whatsapp_number) {
          setWhatsappNumber(res.data.whatsapp_number);
        }
      })
      .catch((err) => console.error('Error fetching settings:', err));
  }, []);

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = 'Name is required';
    if (!form.phone.trim())   e.phone   = 'Phone number is required';
    else if (!/^[0-9+\-\s]{7,15}$/.test(form.phone)) e.phone = 'Enter a valid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (!form.message.trim()) e.message = 'Please write your message';
    return e;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setErrors(e); return; }

    setSubmitting(true);
    setServerError('');
    try {
      const res = await submitInquiry(form);
      if (res.success) {
        setSubmitted(true);
        setInquiryNo(res.inquiry_no || '');
        setForm(INITIAL_FORM);
      } else {
        setServerError(res.message || 'Something went wrong. Please try again.');
      }
    } catch {
      setServerError('Network error. Please try again or call us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Header />

      {/* Banner */}
      <section className="page-banner contact-banner">
        <div className="page-banner__overlay">
          <div className="container">
            <h1 className="page-banner__title">Contact Us</h1>
            <nav className="breadcrumb">
              <a href="/">Home</a>
              <span>/</span>
              <span>Contact</span>
            </nav>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section cp-info-section">
        <div className="container">
          <div className="cp-info-grid">
            <div className="cp-info-card">
              <div className="cp-info-card__icon">📍</div>
              <h3>Our Address</h3>
              <p>Near Maa Sharda Public School,<br />Main Road Kathadih,<br />Mathpurena, Raipur</p>
            </div>
            <div className="cp-info-card">
              <div className="cp-info-card__icon">📞</div>
              <h3>Phone</h3>
              <p><a href="tel:+919981206128">+91 99812 06128</a></p>
              <p><a href="tel:+916264573743">+91 62645 73743</a></p>
              <p className="cp-info-card__sub">Mon–Sat: 10:00 AM – 5:00 PM</p>
            </div>
            <div className="cp-info-card">
              <div className="cp-info-card__icon">✉️</div>
              <h3>Email</h3>
              <p><a href="mailto:heavengreeninfra0@gmail.com">heavengreeninfra0@gmail.com</a></p>
              <p><a href="mailto:heavengreenprojects@gmail.com">heavengreenprojects@gmail.com</a></p>
            </div>
            <div className="cp-info-card">
              <div className="cp-info-card__icon">💬</div>
              <h3>WhatsApp</h3>
              <p>
                <a href={`https://wa.me/${whatsappNumber}?text=Hello, I'd like to inquire about your landscaping services.`} target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
              </p>
              <p className="cp-info-card__sub">Quick response guaranteed</p>
            </div>
          </div>
        </div>
      </section>

      {/* Form + Map */}
      <section className="section cp-main-section">
        <div className="container">
          <div className="cp-layout">

            {/* Inquiry Form */}
            <div className="cp-form-wrap">
              <div className="section-header section-header--left">
                <span className="section-tag">Get In Touch</span>
                <h2 className="section-title">Send Us an Inquiry</h2>
                <p className="section-subtitle">Fill the form below and we'll get back to you within 24 hours.</p>
              </div>

              {submitted ? (
                <div className="cp-success">
                  <div className="cp-success__icon">✅</div>
                  <h3>Inquiry Submitted!</h3>
                  {inquiryNo && <p className="cp-success__no">Your Inquiry No: <strong>{inquiryNo}</strong></p>}
                  <p>Thank you for contacting Heaven Green Infra &amp; Landscape. Our team will reach out to you shortly.</p>
                  <button className="btn-primary" onClick={() => setSubmitted(false)}>
                    Submit Another Inquiry
                  </button>
                </div>
              ) : (
                <div className="cp-form">
                  {serverError && <div className="cp-form__server-error">{serverError}</div>}

                  <div className="cp-form__row">
                    <div className="cp-form__field">
                      <label>Full Name <span>*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Your full name"
                        className={errors.name ? 'error' : ''}
                      />
                      {errors.name && <span className="cp-form__error">{errors.name}</span>}
                    </div>
                    <div className="cp-form__field">
                      <label>Phone Number <span>*</span></label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+91 XXXXX XXXXX"
                        className={errors.phone ? 'error' : ''}
                      />
                      {errors.phone && <span className="cp-form__error">{errors.phone}</span>}
                    </div>
                  </div>

                  <div className="cp-form__row">
                    <div className="cp-form__field">
                      <label>Email Address</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="your@email.com"
                        className={errors.email ? 'error' : ''}
                      />
                      {errors.email && <span className="cp-form__error">{errors.email}</span>}
                    </div>
                    <div className="cp-form__field">
                      <label>Inquiry Type</label>
                      <select name="inquiry_type" value={form.inquiry_type} onChange={handleChange}>
                        <option value="general">General Inquiry</option>
                        <option value="service">Service Inquiry</option>
                        <option value="product">Product Inquiry</option>
                      </select>
                    </div>
                  </div>

                  <div className="cp-form__field">
                    <label>Subject</label>
                    <input
                      type="text"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      placeholder="What is this about?"
                    />
                  </div>

                  <div className="cp-form__field">
                    <label>Message <span>*</span></label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Describe your project or requirement..."
                      rows={5}
                      className={errors.message ? 'error' : ''}
                    />
                    {errors.message && <span className="cp-form__error">{errors.message}</span>}
                  </div>

                  <button
                    className="btn-primary cp-form__submit"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? 'Submitting...' : 'Submit Inquiry'}
                  </button>
                </div>
              )}
            </div>

            {/* Map + Quick Info */}
            <div className="cp-map-wrap">
              <div className="section-header section-header--left">
                <span className="section-tag">Find Us</span>
                <h2 className="section-title">Our Location</h2>
              </div>

              <div className="cp-map">
                <iframe
                  title="Heaven Green Infra Location"
                  src="https://www.google.com/maps?q=Near+Maa+Sharda+Public+School+Main+Road+Kathadih+Mathpurena+Raipur&output=embed"
                  width="100%"
                  height="350"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>

              <div className="cp-quick-info">
                <div className="cp-quick-info__item">
                  <span className="cp-quick-info__label">🕐 Business Hours</span>
                  <span>Monday – Saturday: 10:00 AM – 5:00 PM</span>
                  <span>Sunday: Closed</span>
                </div>
                <div className="cp-quick-info__item">
                  <span className="cp-quick-info__label">📌 Service Areas</span>
                  <span>Raipur, Bilaspur, Bhilai, Durg and across Chhattisgarh</span>
                </div>
                <div className="cp-quick-info__item">
                  <span className="cp-quick-info__label">⚡ Response Time</span>
                  <span>We respond to all inquiries within 24 hours</span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default ContactPage;